import React, {useRef, useState} from 'react';
import {Fab, Popover, List, Divider, ListItemText, ListItemIcon, ListItemButton} from '@mui/material';
import {Event, FileCopy, GetApp} from '@mui/icons-material';
import styled from 'styled-components';
import {useScrollDirection} from 'app/hooks/useScrollDirection';
import {COLORS, SIZES, Typography} from '@fupa/fupa-uikit';
import {GoogleCalendar} from 'app/components/icons/icons';
import {updateSnackbarInfo} from 'app/components/snackbar/SnackbarActions';
import {useDispatch} from 'react-redux';
import {zIndexFab} from 'app/styles/cssConstants';
import {desktopContentWidth} from 'app/styles/media';
import {useNative} from 'app/contexts/NativeContext';

const fabExtendedWidth = 216;
const threshold = 1;
const maxWidthFabExtendedPlaced = desktopContentWidth + 2 * fabExtendedWidth + threshold;

const Container = styled.div`
  display: ${props => (props.show ? 'flex' : 'none')};
  z-index: ${props => props.theme.zIndexFab};
  bottom: 4rem;
  right: 0;
  position: fixed;
  align-items: flex-end;
  justify-content: flex-end;

  ${props => props.theme.desktopLayout`
    display: flex;
    bottom: 0.5rem;
  `};

  @media only screen and (min-width: ${maxWidthFabExtendedPlaced}px) {
    margin-left: ${desktopContentWidth}px;
    right: unset;
  }
`;

const StyledTypography = styled(Typography)`
  text-transform: uppercase;
  color: ${COLORS.white};
  font-weight: bold;
`;

const MatchesICLFab = ({teamSlug}) => {
  const scrollDirection = useScrollDirection();
  const [open, setOpen] = useState(false);
  const fabRef = useRef(null);
  const dispatch = useDispatch();
  const {platform} = useNative();
  const isAndroid = platform === 'android';

  const handleClose = () => setOpen(false);
  const handleFabClick = () => {
    setOpen(true);
  };

  const getUrl = webcal =>
    `${webcal ? process.env.API_URL.replace('https', 'webcal') : process.env.API_URL}${
      process.env.API_DEFAULT_VERSION
    }/teams/${teamSlug}/matches.ics`;

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(getUrl(true))
      .then(() => dispatch(updateSnackbarInfo({message: 'In Zwischenablage kopiert.'})))
      .catch(() =>
        dispatch(updateSnackbarInfo({message: 'Die URL konnte nicht in die Zwischenablage kopiert werden.'}))
      );
  };

  const downloadCalendarFile = () => {
    window.open(getUrl());
  };

  return (
    <Container show={scrollDirection === 'up'}>
      <Popover
        anchorEl={fabRef.current}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        open={open}
        onClose={handleClose}
        style={{zIndex: zIndexFab}}>
        <List>
          <ListItemButton
            component='a'
            href={`https://www.google.com/calendar/render?cid=${getUrl(true)}`}
            target='_blank'>
            <ListItemIcon>
              <GoogleCalendar />
            </ListItemIcon>
            <ListItemText primary='Zu Google Kalender hinzufügen' />
          </ListItemButton>
          <Divider />
          {isAndroid ? null : (
            <>
              <ListItemButton onClick={copyToClipboard}>
                <ListItemIcon>
                  <FileCopy />
                </ListItemIcon>
                <ListItemText primary='Kalender-URL kopieren (iCal-Format)' />
              </ListItemButton>
              <Divider />
            </>
          )}
          <ListItemButton onClick={downloadCalendarFile}>
            <ListItemIcon>
              <GetApp />
            </ListItemIcon>
            <ListItemText primary='Kalender-Datei herunterladen (.ics)' />
          </ListItemButton>
        </List>
      </Popover>
      <Fab
        ref={fabRef}
        variant='extended'
        size='small'
        color='secondary'
        aria-label='add'
        sx={{margin: '1rem'}}
        onClick={handleFabClick}
        style={{height: SIZES[32]}}>
        <Event sx={{fontSize: 20, marginRight: SIZES[4]}} />
        <StyledTypography variant='caption1'>zu Kalender hinzufügen</StyledTypography>
      </Fab>
    </Container>
  );
};

export {MatchesICLFab};
